import { io, Socket } from 'socket.io-client';
import type { WhatsAppAccountStatus, WhatsAppMessage } from '@/types';
import type { MessageStatus } from '@/lib/whatsapp-api';
import type {
  WhatsAppCallIncomingPayload,
  WhatsAppCallCancelledPayload,
  WhatsAppCallClaimedPayload,
} from '@/lib/whatsapp-calls-types';

// Socket event types (matching server)
interface ServerToClientEvents {
  'account:qr': (data: { accountId: string; qrCode: string }) => void;
  'account:status': (data: { accountId: string; status: WhatsAppAccountStatus; phoneNumber?: string }) => void;
  'message:received': (data: { accountId: string; conversationId: string; message: WhatsAppMessage }) => void;
  // A message that already exists changed in place (Phase 3/5: a reaction folded
  // onto its target). Distinct from message:received on purpose — the thread
  // REPLACES on updated and appends on received, so re-announcing a patched row
  // as "received" would duplicate the bubble.
  'message:updated': (data: { accountId: string; conversationId: string; message: WhatsAppMessage }) => void;
  'message:status': (data: { accountId: string; messageId: string; status: MessageStatus }) => void;
  // WhatsApp (waxum) calls — emitted to room `whatsapp:account:{id}`, i.e. the
  // same targeting as `message:received`: every assigned user rings.
  'call:incoming': (data: WhatsAppCallIncomingPayload) => void;
  'call:cancelled': (data: WhatsAppCallCancelledPayload) => void;
  'call:claimed': (data: WhatsAppCallClaimedPayload) => void;
}

// No client-initiated room management -- all room joins are server-side
interface ClientToServerEvents {
  // Intentionally empty
}

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Socket instance (singleton)
let socket: TypedSocket | null = null;

// Get or create socket connection with user's Sanctum token
export function getSocket(token?: string): TypedSocket {
  if (!socket) {
    // Connect to the current origin so requests go through the nginx proxy
    // (/socket.io → whatsapp:3001). Falls back to an explicit URL only if
    // NEXT_PUBLIC_WHATSAPP_SERVICE_URL is set (useful in local dev).
    const socketUrl = process.env.NEXT_PUBLIC_WHATSAPP_SERVICE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : undefined);

    socket = io(socketUrl as string, {
      auth: {
        token: token || '',
      },
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    bindCallDispatchers(socket);
  }

  return socket;
}

// Call-event handlers live OUTSIDE the socket instance: SocketProvider calls
// resetSocket() (removeAllListeners + new instance) when it hydrates or the
// token changes, which happens AFTER the WhatsAppCallProvider has subscribed —
// listeners bound directly to the old instance are silently wiped and inbound
// rings never reach the UI. Each new instance re-binds these dispatchers, so
// subscriptions survive any number of resets.
const callHandlers = {
  incoming: new Set<(data: WhatsAppCallIncomingPayload) => void>(),
  cancelled: new Set<(data: WhatsAppCallCancelledPayload) => void>(),
  claimed: new Set<(data: WhatsAppCallClaimedPayload) => void>(),
};

function bindCallDispatchers(s: TypedSocket): void {
  s.on('call:incoming', (data) => callHandlers.incoming.forEach((cb) => cb(data)));
  s.on('call:cancelled', (data) => callHandlers.cancelled.forEach((cb) => cb(data)));
  s.on('call:claimed', (data) => callHandlers.claimed.forEach((cb) => cb(data)));
}

// Reset socket singleton (needed when token changes on login/logout)
export function resetSocket(): void {
  if (socket) {
    if (socket.connected) {
      socket.disconnect();
    }
    socket.removeAllListeners();
    socket = null;
  }
}

// Connect to socket server
export function connectSocket(): void {
  const s = getSocket();
  if (!s.connected) {
    const resolvedUrl = process.env.NEXT_PUBLIC_WHATSAPP_SERVICE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : 'unknown');
    console.log('[Socket] Initiating connection to', resolvedUrl);
    s.connect();
  } else {
    console.log('[Socket] Already connected');
  }
}

// Disconnect from socket server
export function disconnectSocket(): void {
  if (socket?.connected) {
    socket.disconnect();
  }
}

// Check if connected
export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

// Event listener helpers
export function onAccountQR(callback: (data: { accountId: string; qrCode: string }) => void): () => void {
  const s = getSocket();
  s.on('account:qr', callback);
  return () => s.off('account:qr', callback);
}

export function onAccountStatus(
  callback: (data: { accountId: string; status: WhatsAppAccountStatus; phoneNumber?: string }) => void
): () => void {
  const s = getSocket();
  s.on('account:status', callback);
  return () => s.off('account:status', callback);
}

export function onMessageReceived(
  callback: (data: { accountId: string; conversationId: string; message: WhatsAppMessage }) => void
): () => void {
  const s = getSocket();
  s.on('message:received', callback);
  return () => s.off('message:received', callback);
}

export function onMessageUpdated(
  callback: (data: { accountId: string; conversationId: string; message: WhatsAppMessage }) => void
): () => void {
  const s = getSocket();
  s.on('message:updated', callback);
  return () => s.off('message:updated', callback);
}

export function onMessageStatus(callback: (data: { accountId: string; messageId: string; status: string }) => void): () => void {
  const s = getSocket();
  s.on('message:status', callback);
  return () => s.off('message:status', callback);
}

// WhatsApp call event helpers (mirror onMessageReceived — same rooms, same
// unsubscribe contract; the WhatsAppCallProvider owns the subscriptions).
export function onCallIncoming(
  callback: (data: WhatsAppCallIncomingPayload) => void
): () => void {
  callHandlers.incoming.add(callback);
  return () => callHandlers.incoming.delete(callback);
}

export function onCallCancelled(
  callback: (data: WhatsAppCallCancelledPayload) => void
): () => void {
  callHandlers.cancelled.add(callback);
  return () => callHandlers.cancelled.delete(callback);
}

export function onCallClaimed(
  callback: (data: WhatsAppCallClaimedPayload) => void
): () => void {
  callHandlers.claimed.add(callback);
  return () => callHandlers.claimed.delete(callback);
}

// Connection event helpers
export function onConnect(callback: () => void): () => void {
  const s = getSocket();
  s.on('connect', callback);
  return () => s.off('connect', callback);
}

export function onDisconnect(callback: (reason: string) => void): () => void {
  const s = getSocket();
  s.on('disconnect', callback);
  return () => s.off('disconnect', callback);
}

export function onConnectError(callback: (error: Error) => void): () => void {
  const s = getSocket();
  s.on('connect_error', callback);
  return () => s.off('connect_error', callback);
}
